import { useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import { PageHeader } from "@/components/control/PageHeader";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Section, Loadable, Empty, Status, Tabbar, TabPanel, useTabs, RowLink, useLoad, fmt, deText } from "@/components/control/kit";

function Kpi({ value, text, tone, testId }) {
  return (
    <div className={`rounded-xl border p-4 ${tone}`} data-testid={testId}>
      <p className="text-2xl font-bold">{value ?? 0}</p><p className="text-xs font-medium">{text}</p>
    </div>
  );
}

export default function Reports() {
  const [status, setStatus] = useState("all");
  const st = useLoad(`/reports?status=${encodeURIComponent(status)}`);
  const c = st.data?.counts || {};
  const tabs = useTabs([
    { value: "all", label: "Alle" }, { value: "offen", label: "Offen" }, { value: "übernommen", label: "Übernommen" },
    { value: "in Bearbeitung", label: "In Bearbeitung" }, { value: "entschieden", label: "Entschieden" }, { value: "gelöst", label: "Gelöst" },
  ], status);
  return (
    <div>
      <PageHeader title="Meldungen & Sicherheit" subtitle="Meldungen mit begrenztem Kontext prüfen, begründet entscheiden und Rückmeldung geben." icon={ShieldAlert}
        actions={<Link to="/meldungen-sicherheit/einsprueche" data-testid="appeals-link" className="rounded-lg border border-brand-purple/30 bg-white px-3 py-2 text-sm font-semibold text-brand-purple hover:bg-secondary">Einsprüche</Link>} />
      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi value={c.kritisch} text="Kritisch und offen" tone="border-red-200 bg-red-50 text-red-700" testId="reports-kpi-critical" />
        <Kpi value={c.offen} text="Offen" tone="border-orange-200 bg-orange-50 text-orange-700" testId="reports-kpi-open" />
        <Kpi value={(c["übernommen"] || 0) + (c.in_bearbeitung || 0) + (c.entschieden || 0)} text="In Bearbeitung" tone="border-orange-200 bg-orange-50/60 text-orange-700" testId="reports-kpi-progress" />
        <Kpi value={c["gelöst"]} text="Gelöst" tone="border-green-200 bg-green-50 text-green-700" testId="reports-kpi-resolved" />
      </div>
      <Tabbar tabs={tabs} onChange={setStatus} testId="reports-status-tabs" />
      <TabPanel tabs={tabs} testId="reports-status-tabs-panel">
      <Section title="Meldungen" testId="reports-section">
        <Loadable state={st}>
          {(d) => (
            <div className="overflow-x-auto">
              <Table data-testid="reports-table">
                <TableHeader><TableRow>
                  <TableHead>Meldung</TableHead><TableHead>Kontext</TableHead><TableHead>Dringlichkeit</TableHead>
                  <TableHead>Zuständig</TableHead><TableHead>Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {d.reports.length === 0 && <TableRow><TableCell colSpan={5}><Empty /></TableCell></TableRow>}
                  {d.reports.map((r) => (
                    <TableRow key={r.id} data-testid={`report-row-${r.id}`}>
                      <TableCell>
                        <RowLink to={`/meldungen-sicherheit/${r.id}`} testId={`report-link-${r.id}`}>{deText(r.title) || r.reason || "Meldung"}</RowLink>
                        <p className="text-xs text-muted-foreground">{fmt(r.created_at)}</p>
                      </TableCell>
                      <TableCell className="text-sm">{deText(r.context) || "–"}</TableCell>
                      <TableCell><Status value={r.severity} /></TableCell>
                      <TableCell className="text-sm">{r.assigned_to?.name || "–"}</TableCell>
                      <TableCell><Status value={r.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Loadable>
      </Section>
      </TabPanel>
    </div>
  );
}
